import currencyCodeOptions from '@/themes/shared/data/constants/currencyOptions';
import yesOrNoOptions from '@/themes/shared/data/constants/yesOrNoOptions';
import {
  ConditionalOperatorArray,
  ConditionalOperatorPrimitive,
  FormFieldSchemaType,
  SchemaMedicalExpenseType,
  SchemaRadioYesOrNo,
  type FormFieldIsVisibleWhenCondition,
  type FormStepSchema,
  type SelectFieldOption,
} from '@/types';
import { generateUniqueIdentifierIds } from '@/utils/common';
import { schemaFieldName } from '../schemaFieldName';
import { schemaStepName } from '../schemaStepName';

interface GenerateNewClaimStepMedicalExpensesFormSchemaParams {
  medicalExpenseIds?: string[];
}

const { expenses } = schemaFieldName;
const { medicalExpenses } = expenses;

let stepMedicalExpensesFormSchema: FormStepSchema = {
  name: schemaStepName.stepMedicalExpenses,
  title: 'Tell us about your medical expenses',
  titleShort: 'Medical expenses',
  lastUpdated: Date.now(),
  form: {
    name: `${schemaStepName.stepMedicalExpenses}Form`,
    fields: [],
  },
};

// Maximum number of repeatable expenses in schema.
const MAX_NUMBER_OF_REPEATABLE_MEDICAL_EXPENSES = 10;

let generatedMedicalExpenseIds = generateUniqueIdentifierIds(
  MAX_NUMBER_OF_REPEATABLE_MEDICAL_EXPENSES,
);

let optionsForMedicalExpenseIds = generatedMedicalExpenseIds.map(expenseId => ({
  label: expenseId,
  value: expenseId,
}));

// This combines and updates the relevant variables in 'global' scope.
function combineAndSetExistingAndGeneratedExpenseIdsForMedicalExpenses(
  existingMedicalExpenseIds: typeof generatedMedicalExpenseIds,
) {
  generatedMedicalExpenseIds = [
    ...existingMedicalExpenseIds,
    ...generateUniqueIdentifierIds(MAX_NUMBER_OF_REPEATABLE_MEDICAL_EXPENSES),
  ].slice(0, MAX_NUMBER_OF_REPEATABLE_MEDICAL_EXPENSES);

  optionsForMedicalExpenseIds = generatedMedicalExpenseIds.map(expenseId => ({
    label: expenseId,
    value: expenseId,
  }));
}

const medicalExpenseTypeOptions: SelectFieldOption<SchemaMedicalExpenseType>[] = [
  {
    label: 'Dental treatment',
    value: SchemaMedicalExpenseType.DentalTreatment,
  },
  {
    label: 'Medical consultation',
    value: SchemaMedicalExpenseType.MedicalConsultation,
  },
  {
    label: 'Medication or medical equipment',
    value: SchemaMedicalExpenseType.MedicationOrMedicalEquipment,
  },
  {
    label: 'Medical treatment',
    value: SchemaMedicalExpenseType.MedicalTreatment,
  },
];

function getFieldNameForIndex(fieldName: string, index: number) {
  return fieldName.replace('{i}', `${index}`);
}

type FormStepSchemaFormFields = FormStepSchema['form']['fields'];
function generateMedicalExpensesFields(): FormStepSchemaFormFields {
  return generatedMedicalExpenseIds.flatMap((expenseId, index) => {
    const isExpenseVisibleWhen: FormFieldIsVisibleWhenCondition[] = [
      {
        field: expenses.hasMedicalExpense,
        value: SchemaRadioYesOrNo.Yes,
        operator: ConditionalOperatorPrimitive.Equal,
      },
      {
        value: [expenseId],
        field: expenses.medicalExpenseIds,
        operator: ConditionalOperatorArray.Includes,
      },
    ];

    return [
      // Q5.1 - Type of medical expense.
      {
        // @TODO: Need to add a collapsable 'information' or 'tooltip'.
        type: FormFieldSchemaType.Select,
        name: getFieldNameForIndex(medicalExpenses.expenseType, index),
        label: "Select the type of medical expense you're claiming for",
        description: 'Want to claim for multiple medical expenses? You can add more below.',
        layoutOptions: {
          hasSeparator: index !== 0,
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
        options: medicalExpenseTypeOptions,
      },
      // Q5.2 - Date of medical expense.
      {
        type: FormFieldSchemaType.Date,
        name: getFieldNameForIndex(medicalExpenses.expenseDate, index),
        label: 'When did you pay for this expense?',
        layoutOptions: {
          width: '1/2-block',
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
      },
      // Q5.3 - Cost of medical expense.
      {
        type: FormFieldSchemaType.Number,
        name: getFieldNameForIndex(medicalExpenses.expenseAmount.amount, index),
        label: 'How much did this expense cost?',
        description: 'Use the amount on the invoice or bill',
        layoutOptions: {
          width: '1/2-inline',
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
      },
      {
        type: FormFieldSchemaType.Select,
        name: getFieldNameForIndex(medicalExpenses.expenseAmount.currencyCode, index),
        label: 'How much did this expense cost?',
        description: 'Use the currency on the invoice or bill',
        layoutOptions: {
          width: '1/2-inline',
          isLabelInvisible: true,
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
        options: currencyCodeOptions,
      },
      // Q5.4 - Was expense paid in full?
      {
        type: FormFieldSchemaType.Radio,
        name: getFieldNameForIndex(medicalExpenses.expensePaidInFull, index),
        label: 'Did you pay for this expense in full?',
        options: yesOrNoOptions,
        layoutOptions: {
          optionsPositioning: 'inline',
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
      },
      // Q5.4.1 - Q5.4.5 - Provider details.
      {
        type: FormFieldSchemaType.Text,
        name: getFieldNameForIndex(medicalExpenses.providerDetails.accountName, index),
        title: 'Tell us who we need to pay and how much',
        text: "Please tell us the medical provider's bank details and the outstanding balance. Enter the payment details exactly as they appear on the invoice or bill.",
        label: 'Account name',
        placeholder: 'Enter account name',
        isRequired: false,
        isVisibleWhen: [
          ...isExpenseVisibleWhen,
          {
            field: getFieldNameForIndex(medicalExpenses.expensePaidInFull, index),
            value: SchemaRadioYesOrNo.No,
            operator: ConditionalOperatorPrimitive.Equal,
          },
        ],
      },
      {
        type: FormFieldSchemaType.Text,
        name: getFieldNameForIndex(medicalExpenses.providerDetails.swiftCode, index),
        label: 'SWIFT code',
        placeholder: 'Enter SWIFT code',
        isRequired: false,
        layoutOptions: {
          width: '1/2-inline',
        },
        isVisibleWhen: [
          ...isExpenseVisibleWhen,
          {
            field: getFieldNameForIndex(medicalExpenses.expensePaidInFull, index),
            value: SchemaRadioYesOrNo.No,
            operator: ConditionalOperatorPrimitive.Equal,
          },
        ],
      },
      {
        type: FormFieldSchemaType.Text,
        name: getFieldNameForIndex(medicalExpenses.providerDetails.ibanNumber, index),
        label: '(OR) IBAN number',
        placeholder: 'Enter IBAN number',
        isRequired: false,
        layoutOptions: {
          width: '1/2-inline',
        },
        isVisibleWhen: [
          ...isExpenseVisibleWhen,
          {
            field: getFieldNameForIndex(medicalExpenses.expensePaidInFull, index),
            value: SchemaRadioYesOrNo.No,
            operator: ConditionalOperatorPrimitive.Equal,
          },
        ],
      },
      // Q5.4.6 - Invoice or order number.
      {
        type: FormFieldSchemaType.Text,
        name: getFieldNameForIndex(medicalExpenses.providerDetails.referenceNumber, index),
        label: 'Invoice or order number',
        placeholder: '123456',
        isRequired: false,
        isVisibleWhen: [
          ...isExpenseVisibleWhen,
          {
            field: getFieldNameForIndex(medicalExpenses.expensePaidInFull, index),
            value: SchemaRadioYesOrNo.No,
            operator: ConditionalOperatorPrimitive.Equal,
          },
        ],
      },
      // Q5.4.7 - Total amount owed.
      {
        type: FormFieldSchemaType.Number,
        name: getFieldNameForIndex(medicalExpenses.providerOwedAmount.amount, index),
        label: 'Enter the total amount owed',
        description: 'Enter amount',
        layoutOptions: {
          width: '1/2-inline',
        },
        isVisibleWhen: [
          ...isExpenseVisibleWhen,
          {
            field: getFieldNameForIndex(medicalExpenses.expensePaidInFull, index),
            value: SchemaRadioYesOrNo.No,
            operator: ConditionalOperatorPrimitive.Equal,
          },
        ],
      },
      {
        type: FormFieldSchemaType.Select,
        name: getFieldNameForIndex(medicalExpenses.providerOwedAmount.currencyCode, index),
        label: 'Enter the total amount owed',
        description: 'Select currency',
        layoutOptions: {
          width: '1/2-inline',
          isLabelInvisible: true,
        },
        isVisibleWhen: [
          ...isExpenseVisibleWhen,
          {
            field: getFieldNameForIndex(medicalExpenses.expensePaidInFull, index),
            value: SchemaRadioYesOrNo.No,
            operator: ConditionalOperatorPrimitive.Equal,
          },
        ],
        options: currencyCodeOptions,
      },
      // ID for this medical expense.
      {
        type: FormFieldSchemaType.Text,
        name: getFieldNameForIndex(medicalExpenses.expenseId, index),
        label: 'Enter medical expense ID',
        value: expenseId,
        isRequired: false,
        layoutOptions: {
          isVisuallyHidden: true,
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
        repeatOptions: {
          action: 'remove',
          label: 'Remove this medical expense',
          field: expenses.medicalExpenseIds,
          value: expenseId,
        },
      },
    ];
  });
}

function generateNewClaimStepMedicalExpensesFormSchema({
  medicalExpenseIds = [],
}: GenerateNewClaimStepMedicalExpensesFormSchemaParams = {}): FormStepSchema {
  combineAndSetExistingAndGeneratedExpenseIdsForMedicalExpenses(medicalExpenseIds);
  const { form, ...otherSchemaProps } = stepMedicalExpensesFormSchema;
  stepMedicalExpensesFormSchema = {
    ...otherSchemaProps,
    lastUpdated: Date.now(),
    form: {
      name: form.name,
      fields: [
        // QNEW - Have medical expense?
        {
          type: FormFieldSchemaType.Radio,
          name: expenses.hasMedicalExpense,
          label: 'Do you have any medical expenses you want to claim?',
          layoutOptions: {
            optionsPositioning: 'inline',
          },
          options: yesOrNoOptions,
        },
        ...generateMedicalExpensesFields(),
        // IDs for tracking medical expenses.
        {
          type: FormFieldSchemaType.Checkbox,
          name: expenses.medicalExpenseIds,
          label: 'Add or remove another medical expense',
          // If there are no expenses currently visible, then first expense is visible by default.
          value: medicalExpenseIds.length ? medicalExpenseIds : [generatedMedicalExpenseIds[0]],
          layoutOptions: {
            isVisuallyHidden: true,
          },
          isVisibleWhen: [
            {
              field: expenses.hasMedicalExpense,
              value: SchemaRadioYesOrNo.Yes,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          options: optionsForMedicalExpenseIds,
          repeatOptions: {
            action: 'add',
            label: 'Add another medical expense',
            values: optionsForMedicalExpenseIds.map(({ value }) => value),
          },
        },
      ],
    },
  };

  return stepMedicalExpensesFormSchema;
}

function getNewClaimStepMedicalExpensesFormSchema() {
  if (!stepMedicalExpensesFormSchema.form.fields.length) {
    stepMedicalExpensesFormSchema = generateNewClaimStepMedicalExpensesFormSchema();
  }

  return stepMedicalExpensesFormSchema;
}

export { generateNewClaimStepMedicalExpensesFormSchema, getNewClaimStepMedicalExpensesFormSchema };

export type { GenerateNewClaimStepMedicalExpensesFormSchemaParams };
